import { Alert, Card, Col, Input, notification, Row, Typography, Button } from "antd";
import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { PrimaryButton } from "../../styled/Button.styled";
import { useNavigate } from "react-router-dom";
import {
  useRequestLoginOtpMutation,
  useVerifyLoginOtpMutation,
} from "../../../feature/login/loginApiSlice";
import secureLocalStorage from "react-secure-storage";
import { logo, textLogo } from "../../../constants/logos";
import { useAppDispatch } from "../../../app/hooks";
import { setToken } from "../../../feature/ui/uiSlice";

const LoginContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  flex-direction: column;
`;

const PHONE_REGEX = /^[6-9]\d{9}$/;
const OTP_LENGTH = 6;
const { Link } = Typography;

const Login = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState("");
  const [requestLoginOtp] = useRequestLoginOtpMutation();
  const [verifyLoginOtp] = useVerifyLoginOtpMutation();
  const [disable, setDisable] = useState(true);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    setDisable(!PHONE_REGEX.test(phoneNumber));
  }, [phoneNumber]);

  useEffect(() => {
    if (isOtpSent) {
      setTimeout(() => inputRefs.current[0]?.focus(), 0);
    }
  }, [isOtpSent]);

  const otp = otpDigits.join("");

  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otpDigits];
    next[index] = digit;
    setOtpDigits(next);
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "Enter" && otp.length === OTP_LENGTH && !isVerifyingOtp) {
      handleVerifyOtp();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setOtpDigits(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleSendOtp = async () => {
    try {
      setIsRequestingOtp(true);
      setError("");
      await requestLoginOtp({ phoneNumber }).unwrap();
      setIsOtpSent(true);
      notification.success({
        message: "OTP Sent",
        description: "An OTP has been sent to your registered phone number.",
        placement: "bottomLeft",
      });
    } catch (error) {
      setError("Failed to send OTP. Please check the number and try again.");
      notification.error({
        message: "Failed to Send OTP",
        placement: "bottomLeft",
      });
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setIsVerifyingOtp(true);
      setOtpError("");
      const data = await verifyLoginOtp({ phoneNumber, otp }).unwrap();
      const { roles, id, accessToken, username } = data;

      secureLocalStorage.setItem("name", username);
      secureLocalStorage.setItem("role", roles.toString());
      secureLocalStorage.setItem("id", id.toString());
      secureLocalStorage.setItem("token", accessToken);

      dispatch(setToken(accessToken));
      navigate("/dashboard");
      notification.success({
        message: "Successfully Logged In!",
        placement: "bottomLeft",
      });
    } catch (error) {
      setOtpError("Invalid or expired OTP. Please try again.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleCancelOtp = () => {
    setIsOtpSent(false);
    setOtpError("");
    setOtpDigits(Array(OTP_LENGTH).fill(""));
  };

  return (
    <>
      <LoginContainer className="bgImage">
        <Row
          justify={"space-between"}
          align={"middle"}
          className="login-container-inner-row"
        >
          <Col className="logo brand-logo-position" span={6}>
            <Row gutter={[0, 12]}>
              <Col span={24}>
                <Row justify={"center"}>
                  <img src={logo} alt="brandLogo" className="brand-logo-size" />
                </Row>
              </Col>
              <Col span={24}>
                <Row justify={"center"}>
                  <img src={textLogo} alt="textLogo" />
                </Row>
              </Col>
            </Row>
          </Col>
          <Col span={11}>
            <Card
              className="login-screen-card"
              bordered={false}
              bodyStyle={{ textAlign: "center", padding: "1rem" }}
              style={{ position: "relative" }}
            >
              <h4 className="welcome-text">Welcome To Himcab</h4>
              <Row justify={"center"} style={{ display: "flex" }}>
                <Col span={24}>
                  <p className="body-text">Login via Registered Phone Number</p>
                </Col>
                {error && <Alert message={error} type="error" style={{ marginBottom: "15px" }} />}
                {!isOtpSent && (
                  <>
                    <Col className="login-email-container mb-1" span={23} style={{ marginBottom: "15px" }}>
                      <Row justify={"start"}>
                        <label className="fs-8 fontColor" htmlFor="phoneNumber" style={{ fontSize: "16px" }}>
                          Phone Number
                        </label>
                      </Row>
                      <Row>
                        <Input
                          placeholder="Enter Phone Number"
                          style={{ height: "40px" }}
                          value={phoneNumber}
                          maxLength={10}
                          onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        />
                      </Row>
                    </Col>
                    {/* <Col span={23} style={{ marginBottom: "15px" }}>
                      <Row justify={"center"}>
                        <Link onClick={() => navigate("/forgot-password")}>
                          Forgot Password?
                        </Link>
                      </Row>
                    </Col> */}
                    <Col className="login-button-container" span={23} style={{ marginBottom: "0px" }}>
                      <Row justify={"center"}>
                        <PrimaryButton
                          disabled={disable}
                          loading={isRequestingOtp}
                          onClick={handleSendOtp}
                          style={{ height: "3rem", maxWidth: "200px", fontSize: "20px" }}
                        >
                          Send OTP
                        </PrimaryButton>
                      </Row>
                    </Col>
                  </>
                )}
                {isOtpSent && (
                  <>
                    <Col span={23} style={{ marginBottom: "15px" }}>
                      <Row justify={"center"}>
                        <p style={{ fontSize: "14px", color: "#666" }}>
                          {otpError || `An OTP has been sent. Enter it below to log in.`}
                        </p>
                      </Row>
                    </Col>
                    <Col span={23} style={{ marginBottom: "15px" }}>
                      <Row justify={"center"} style={{ display: "flex", gap: "8px" }}>
                        {otpDigits.map((digit, index) => (
                          <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleOtpChange(index, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            onPaste={handleOtpPaste}
                            disabled={isVerifyingOtp}
                            style={{
                              width: "44px",
                              height: "48px",
                              textAlign: "center",
                              fontSize: "20px",
                              border: "1px solid #d9d9d9",
                              borderRadius: "6px",
                              outline: "none",
                            }}
                          />
                        ))}
                      </Row>
                    </Col>
                    <Col span={23} style={{ marginBottom: "15px" }}>
                      <Row justify={"center"}>
                        <Button type="link" onClick={handleSendOtp} disabled={isVerifyingOtp}>
                          Resend OTP
                        </Button>
                      </Row>
                    </Col>
                    <Col className="login-button-container mb-1" span={23}>
                      <Row justify={"center"}>
                        <PrimaryButton
                          disabled={otp.length !== OTP_LENGTH || isVerifyingOtp}
                          loading={isVerifyingOtp}
                          onClick={handleVerifyOtp}
                          style={{ height: "3rem", maxWidth: "200px", fontSize: "20px" }}
                        >
                          Login
                        </PrimaryButton>
                      </Row>
                    </Col>
                  </>
                )}
              </Row>
            </Card>
          </Col>
        </Row>
      </LoginContainer>
    </>
  );
};

export default Login;
